import { ReactNode, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  FileText,
  MessageCircle,
  BarChart3,
  Settings,
  LogOut,
  ChevronDown,
  Shield,
  Menu,
  Headphones,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAgent } from "@/contexts/AgentContext";
import { useAuth } from "@/hooks/useAuth";
import { useAdmin } from "@/hooks/useAdmin";
import { NotificationBell } from "@/components/dashboard/NotificationBell";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";
import { useIsMobile } from "@/hooks/use-mobile";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { ViewAsClientBanner } from "@/components/dashboard/ViewAsClientBanner";
import logo from "@/assets/logo.png";

interface DashboardLayoutProps {
  children: ReactNode;
}

const navigation = [
  { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
  { name: "My Leads", href: "/dashboard/leads", icon: Users },
  { name: "Delivery Settings", href: "/dashboard/delivery-settings", icon: Shield },
  { name: "Contract & Billing", href: "/dashboard/wallet", icon: FileText },
  { name: "Performance", href: "/dashboard/performance", icon: BarChart3 },
  { name: "Messages", href: "/dashboard/messages", icon: MessageCircle },
  { name: "Settings", href: "/dashboard/settings", icon: Settings },
];

const DashboardLayoutInner = ({ children }: DashboardLayoutProps) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { state } = useAgent();

  // Phase D: preserve viewAs query param across sidebar navigation
  const viewAsSuffix = searchParams.get("viewAs") ? `?viewAs=${searchParams.get("viewAs")}` : "";
  const { signOut } = useAuth();
  const { isAdmin, role, isAccountManager } = useAdmin();
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { unreadCount: unreadMessages } = useUnreadMessages();

  const first = state.profile.firstName || state.profile.name?.split(" ")[0] || "";
  const last = state.profile.lastName || state.profile.name?.split(" ").slice(1).join(" ") || "";
  const initials = `${first[0] || "?"}${last[0] || ""}`.toUpperCase();
  const verticalLabel = state.profile.vertical === "life_insurance" ? "Life Insurance" : "Mortgage";
  const isDemo = state.profile.isDemo;

  const navContent = (
    <>
      <nav className="flex-1 px-4 py-6 space-y-1.5 overflow-y-auto">
        {navigation.map((item) => {
          const isActive = location.pathname === item.href;
          return (
            <Link
              key={item.name}
              to={`${item.href}${viewAsSuffix}`}
              onClick={() => isMobile && setSidebarOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                isActive
                  ? "bg-sidebar-accent text-sidebar-primary"
                  : "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
              }`}
            >
              <item.icon className={`w-5 h-5 ${isActive ? "text-sidebar-primary" : ""}`} />
              {item.name}
              {item.name === "Messages" && unreadMessages > 0 && (
                <span className="ml-auto min-w-[20px] h-5 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5">
                  {unreadMessages > 99 ? "99+" : unreadMessages}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      <div className="p-4 border-t border-sidebar-border">
        <div className="flex items-center gap-3 px-3 py-2 rounded-xl bg-sidebar-accent/30">
          <div className="w-10 h-10 rounded-full bg-sidebar-primary flex items-center justify-center text-sidebar-primary-foreground font-semibold">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-sidebar-foreground truncate">{state.profile.name}</p>
            <p className="text-xs text-sidebar-foreground/60 truncate">{verticalLabel}</p>
          </div>
          <ChevronDown className="w-4 h-4 text-sidebar-foreground/60" />
        </div>

        {isAdmin && isAccountManager && (
          <Link
            to="/am"
            onClick={() => isMobile && setSidebarOpen(false)}
            className="flex items-center gap-3 w-full mt-3 px-4 py-2.5 rounded-xl text-sm font-medium text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/30 transition-all duration-200"
          >
            <Headphones className="w-4 h-4" />
            AM Portal
          </Link>
        )}
        {isAdmin && role !== "account_manager" && (
          <Link
            to={role === "sales" ? "/admin/contracts" : "/admin"}
            onClick={() => isMobile && setSidebarOpen(false)}
            className="flex items-center gap-3 w-full mt-3 px-4 py-2.5 rounded-xl text-sm font-medium text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/30 transition-all duration-200"
          >
            <Shield className="w-4 h-4" />
            Admin Panel
          </Link>
        )}
        <Button
          variant="ghost"
          onClick={() => signOut()}
          className="w-full mt-1 text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/30 justify-start gap-3"
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar */}
      {!isMobile && (
        <aside className="fixed inset-y-0 left-0 w-64 bg-sidebar border-r border-sidebar-border flex flex-col">
          <div className="h-20 flex items-center px-6 border-b border-sidebar-border">
            <Link to={`/dashboard${viewAsSuffix}`}>
              <img src={logo} alt="Trusted James" className="h-9 w-auto" />
            </Link>
          </div>
          {navContent}
        </aside>
      )}

      {/* Mobile Sidebar Sheet */}
      {isMobile && (
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetContent side="left" className="w-72 p-0 bg-sidebar border-sidebar-border flex flex-col">
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <div className="h-16 flex items-center px-6 border-b border-sidebar-border">
              <Link to={`/dashboard${viewAsSuffix}`} onClick={() => setSidebarOpen(false)}>
                <img src={logo} alt="Trusted James" className="h-8 w-auto" />
              </Link>
            </div>
            {navContent}
          </SheetContent>
        </Sheet>
      )}

      {/* Main Content */}
      <div className={`${isMobile ? "w-full" : "pl-64"} flex-1`}>
        {/* Phase D: Admin "View as Client" banner */}
        <ViewAsClientBanner />
        {/* Top Bar */}
        <header className={`h-16 md:h-20 bg-card border-b border-border flex items-center justify-between ${isMobile ? "px-4" : "px-8"}`}>
          <div className="flex items-center gap-3">
            {isMobile && (
              <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)} className="mr-1">
                <Menu className="h-5 w-5" />
              </Button>
            )}
            <div>
              <h1 className={`font-display ${isMobile ? "text-lg" : "text-2xl"} font-bold text-foreground`}>Dashboard</h1>
              {!isMobile && <p className="text-sm text-muted-foreground">Welcome back, {first || "Agent"}</p>}
            </div>
            {isDemo && (
              <span className="text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 px-2.5 py-1 rounded-full">
                DEMO
              </span>
            )}
          </div>

          <div className="flex items-center gap-4">
            <NotificationBell />
          </div>
        </header>

        {/* Page Content */}
        <main className={`${isMobile ? "p-4" : "p-8"} overflow-x-hidden`}>{children}</main>
      </div>
    </div>
  );
};

export default DashboardLayoutInner;
